import { clientEnv } from 'config/env';
import { createValidationDataObject } from 'app/utilities/form-validation';
import { ENDPOINTS } from 'config/api';
import fetch from 'app/utilities/fetch';
import Moment from 'moment';
import { EnquiryForm, getInitialValues, SUBMIT_FAILURE_STATUS, SUBMIT_SUCCESS_STATUS } from 'app/components/partials/enquiry-form';
import React, { useState } from 'react';

const MIN_MESSAGE_LENGTH = 10;

const ENQUIRY_FORM_FIELDS = [
    {
        name: 'firstName',
        label: 'First name*',
        placeholder: '',
        type: 'text',
        width: 'half',
        validator: (firstName) => {
            if (firstName) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'lastName',
        label: 'Last name*',
        placeholder: '',
        type: 'text',
        width: 'half',
        validator: (lastName) => {
            if (lastName) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'jobTitle',
        label: 'Job Title',
        placeholder: '',
        type: 'text',
        width: 'half',
        validator: () => {
            return createValidationDataObject();
        }
    },
    {
        name: 'phone',
        label: 'Phone*',
        placeholder: '',
        type: 'tel',
        width: 'half',
        validator: (phone) => {
            if (phone) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'email',
        label: 'Email*',
        placeholder: '',
        type: 'email',
        width: 'half',
        validator: (email) => {
            if (email) {
                // https://stackoverflow.com/questions/46155/how-to-validate-email-address-in-javascript
                if (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email)) { // eslint-disable-line
                    return createValidationDataObject();
                }

                return createValidationDataObject(false, 'Not a valid email address');
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'company',
        label: 'Company*',
        placeholder: '',
        type: 'text',
        width: 'half',
        validator: (company) => {
            if (company) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'workWithUsBefore',
        label: 'Have you worked with us before?',
        placeholder: '',
        type: 'checkbox',
        width: 'full',
        validator: () => {
            return createValidationDataObject();
        }
    },
    {
        name: 'guestNumbers',
        label: 'Guest Numbers*',
        placeholder: 'e.g. 100',
        type: 'number',
        width: 'half',
        validator: (guestNumbers) => {
            if (guestNumbers) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'eventName',
        label: 'Event Name',
        placeholder: '',
        type: 'text',
        width: 'half',
        validator: () => {
            return createValidationDataObject();
        }
    },
    {
        name: 'eventType',
        label: 'Event Type*',
        placeholder: '',
        type: 'single-select',
        width: 'half',
        options: [
            { value: null, label: 'Please select' },
            { value: 'Conference', label: 'Conference' },
            { value: 'Exhibition', label: 'Exhibition' },
            { value: 'Meeting/Seminar', label: 'Meeting/Seminar' },
            { value: 'Dinner/Gala', label: 'Dinner/Gala' },
            { value: 'Cocktail Party', label: 'Cocktail Party' },
            { value: 'Breakfast/Lunch', label: 'Breakfast/Lunch' },
            { value: 'Graduation/Awards Ceremony', label: 'Graduation/Awards Ceremony' },
            { value: 'Product Launch', label: 'Product Launch' },
            { value: 'School Ball', label: 'School Ball' },
            { value: 'Christmas Function', label: 'Christmas Function' },
            { value: 'Wedding', label: 'Wedding' },
            { value: 'Team Building', label: 'Team Building' },
            { value: 'Other', label: 'Other' },

        ],
        validator: (eventType) => {
            if (eventType) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'otherEventType',
        label: 'Please Specify Event Type*',
        placeholder: '',
        type: 'text',
        width: 'half',
        showIf: (allFields = {}) => allFields.eventType === 'Other',
        validator: (otherEventType, allFields = {}) => {
            if (allFields && allFields.eventType === 'Other') {
                if (!otherEventType || String(otherEventType).trim().length === 0) {
                    return createValidationDataObject(false, 'This is a required field');
                }
            }

            return createValidationDataObject();
        }
    },
    {
        name: 'preferredVenue',
        label: 'Preferred Venue*',
        placeholder: 'You can choose multiple venues',
        type: 'multi-select',
        options: () => fetch(ENDPOINTS.VENUES_ALL),
        width: 'full',
        validator: (preferredVenue) => {
            if (!preferredVenue || preferredVenue.length === 0) {
                return createValidationDataObject(false, 'This is a required field');
            }

            return createValidationDataObject();
        }
    },
    {
        name: 'preferredStartDate',
        label: 'Preferred Start Date*',
        placeholder: '',
        type: 'date',
        width: 'half',
        min: Moment().format('YYYY-MM-DD'),
        validator: (preferredStartDate) => {
            if (preferredStartDate) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'preferredEndDate',
        label: 'Preferred End Date*',
        placeholder: '',
        type: 'date',
        width: 'half',
        min: Moment().format('YYYY-MM-DD'),
        minField: 'preferredStartDate', // dynamically set this min date based on another field value
        validator: (preferredEndDate) => {
            if (preferredEndDate) {
                return createValidationDataObject();
            }

            return createValidationDataObject(false, 'This is a required field');
        }
    },
    {
        name: 'dateFlexibility',
        label: 'Are your dates flexible?',
        type: 'checkbox',
        width: 'full',
        validator: () => createValidationDataObject()
    },
    {
        name: 'message',
        label: 'Event Information*',
        placeholder: 'Please provide any additional information about your event that will help us to assist you',
        type: 'multi-line',
        width: 'full',
        validator: (message) => {
            if (message.length < MIN_MESSAGE_LENGTH) {
                return createValidationDataObject(false, 'Please provide more information');
            }

            if (!message) {
                return createValidationDataObject(false, 'This is a required field');
            }

            return createValidationDataObject();
        }
    },
    {
        name: 'marketingOptIn',
        label: 'Yes, I would like to receive news and updates from Auckland Conventions Venues & Events.',
        type: 'checkbox',
        width: 'full',
        validator: () => createValidationDataObject()
    },
];

const ContactForm = () => {
    const [formValues, setFormValues] = useState(getInitialValues(ENQUIRY_FORM_FIELDS));
    const [formErrors, setFormErrors] = useState({});
    const [responseStatus, setResponseStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateField = (field, value) => {
        if (field.validator) {
            const result = field.validator(value, formValues);
            if (!result.valid) {
                return result.message;
            }
        }

        return '';
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const errors = {};
        ENQUIRY_FORM_FIELDS.forEach((field) => {
            const error = validateField(field, formValues[field.name]);
            if (error) errors[field.name] = error;
        });
        setFormErrors(errors);
        if (Object.keys(errors).length === 0) {
            setIsSubmitting(true);
            // eslint-disable-next-line no-undef
            grecaptcha.ready(function() {
                // eslint-disable-next-line no-undef
                grecaptcha.execute(clientEnv.RECAPTCHA_KEY, { action: 'submit' }).then(function(token) {
                    submitForm(formValues, token);
                });
            });
        }
    };

    const submitForm = (values, token) => {
        values.captchaToken = token; // Add reCAPTCHA token to the form values
        fetch(ENDPOINTS.ENQUIRY, null, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(values)
        }).then(() => {
            setResponseStatus(SUBMIT_SUCCESS_STATUS);
            setFormValues(getInitialValues(ENQUIRY_FORM_FIELDS)); // Reset form values
        }).catch(() => {
            setResponseStatus(SUBMIT_FAILURE_STATUS);
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <EnquiryForm type="contact-form" formFields={ENQUIRY_FORM_FIELDS} formValues={formValues} setFormValues={setFormValues} formErrors={formErrors} setFormErrors={setFormErrors} validateField={validateField} responseStatus={responseStatus} handleSubmit={handleSubmit} isSubmitting={isSubmitting} />
    );
};

export default ContactForm;
